import { useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as UserIcon } from '@elementary/common-ui/assets/16x16/user.svg';
import { ReactComponent as MergeIcon } from '@elementary/common-ui/assets/20x20/merge.svg';
import { ReactComponent as ArrowDownIcon } from '@elementary/common-ui/assets/arrow-down-expand.svg';
import {
  Avatar,
  ChipCounterList,
  DropDown,
  Flex,
  IconButton,
  Menu,
  OverflowTooltip,
  StatusChip,
  SvgIcon,
} from '@elementary/common-ui/components';
import type { ChipStatus } from '@elementary/common-ui/components/Chip/StatusChip';
import { Option } from '@elementary/common-ui/components/Menu/Menu';
import { Column, ColumnType } from '@elementary/common-ui/components/Table';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { Link, Tooltip, Typography } from '@mui/material';
import {
  TypeChip,
  TypeChipLabel,
} from '@elementary/common-ui/components/cellRenderers/typeChip';
import {
  extractTicketIdFromUrl,
  ticketingIconMap,
} from 'shared/components/ticketing/utils';
import { FetchTeamUsersApi } from 'shared/types';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import type { Severity, Status } from 'incidents/types';
import {
  getGroupingStrategyDisplay,
  severityIconMap,
  smallStatusIconMap,
} from 'incidents/utils';
import {
  fontSize,
  fontWeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';
import {
  GROUPING_STRATEGY_COLUMN_ID,
  SEVERITY_DISPLAY,
  STATUS_DISPLAY,
} from '../constants';
import { getMonitorChipStatus } from '../statusUtils';
import type {
  IncidentListItem,
  IncidentListMonitorRow,
  IncidentListTicket,
} from '../types';
import IncidentActionsColumnMenu from './IncidentActionsColumnMenu';

const FROZEN_COLUMN_WIDTH = 74;

const MONITOR_CHIP_ORDER: ChipStatus[] = [
  'error',
  'fail',
  'runtime_error',
  'warn',
  'warning',
  'pass',
  'passed',
  'success',
  'skipped',
  'skip',
  'noData',
  'idle',
  'resolved',
];

function countMonitorsByChipStatus(
  monitors: IncidentListMonitorRow[],
): Map<ChipStatus, number> {
  const counts = new Map<ChipStatus, number>();
  for (const m of monitors) {
    const chip = getMonitorChipStatus(m.signal);
    counts.set(chip, (counts.get(chip) ?? 0) + 1);
  }
  return counts;
}

const Label = styled(Typography)`
  line-height: normal;
  white-space: nowrap;
`;

const TicketChipLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${space('xs')};
  padding: ${space('xxs', 'smMinus')};
  border-radius: ${radius('sm')};
  border: 1px solid ${({ theme }) => theme.palette.border.default};
  font-size: ${fontSize('sm')};
  line-height: normal;
  white-space: nowrap;
  text-decoration: none;
  color: ${({ theme }) => theme.palette.content.translucent.primary};
  min-width: 0;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.palette.surface.translucent.hover};
  }
`;

const getTicketDetails = (ticket: IncidentListTicket) => {
  const key = ticket.service.toLowerCase().replace('_', '');
  const icon = ticketingIconMap[key];
  const label =
    extractTicketIdFromUrl(
      ticket.serviceTicketUrl,
      key,
      ticket.serviceTicketId,
    ) || ticket.serviceTicketId;
  return { key, icon, label };
};

type TicketChipCompactProps = {
  ticket: IncidentListTicket;
};

const TicketLabel = styled('span')`
  font-size: ${fontSize('sm')};
  line-height: normal;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TicketChipCompact = ({ ticket }: TicketChipCompactProps) => {
  const { icon, label } = getTicketDetails(ticket);

  return (
    <TicketChipLink
      href={ticket.serviceTicketUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      {icon ? <SvgIcon width={14}>{icon}</SvgIcon> : null}
      <TicketLabel>{label}</TicketLabel>
    </TicketChipLink>
  );
};

const OptionalChipCounterList = ({ items }: { items?: string[] }) => {
  if (!items?.length) return <Label>—</Label>;
  return <ChipCounterList items={items} />;
};

const IconButtonStyled = styled(IconButton)<{ $readOnly?: boolean }>`
  padding: ${space('smMinus')};
  pointer-events: ${({ $readOnly }) => ($readOnly ? 'none' : 'auto')};
`;

type Props = {
  severityOptions?: Option[];
  statusOptions?: Option[];
  assigneeOptions?: Option[];
  usersData?: FetchTeamUsersApi;
  canEdit: boolean;
  isAiIncidentsEnabled?: boolean;
  hasTicketingIntegration?: boolean;
  onOpenAIChat: (
    id: string,
    startMessage: string,
    displayLabel: string,
  ) => void;
  onChange: (
    id: string,
    val: { assignee?: string; status?: string; severity?: string },
  ) => void;
};

const useIncidentColumns = ({
  severityOptions,
  statusOptions,
  assigneeOptions,
  usersData,
  canEdit,
  isAiIncidentsEnabled,
  hasTicketingIntegration,
  onOpenAIChat,
  onChange,
}: Props): Column<IncidentListItem>[] =>
  useMemo<Column<IncidentListItem>[]>(
    () => [
      {
        id: 'severity',
        header: 'Severity',
        width: 100,
        sortable: true,
        filterable: false,
        renderer: (incident) => {
          const displaySev =
            SEVERITY_DISPLAY[incident.severity.toLowerCase()] ?? 'Normal';
          return (
            <Menu
              tooltip={canEdit ? 'Set severity' : displaySev}
              menuButton={
                <IconButtonStyled
                  $readOnly={!canEdit}
                  tabIndex={canEdit ? undefined : -1}
                  disabled={canEdit && incident.status === 'RESOLVED'}
                >
                  <Flex sx={{ alignItems: 'center', gap: 1 }}>
                    {severityIconMap[displaySev as Severity]}
                    {canEdit && (
                      <SvgIcon
                        width={10}
                        strokeColor="icon.tertiary"
                        fillColor="icon.tertiary"
                      >
                        <ArrowDownIcon />
                      </SvgIcon>
                    )}
                  </Flex>
                </IconButtonStyled>
              }
              menuTitle="SET SEVERITY"
              options={severityOptions}
              closeOnChange
              onChange={(val) =>
                onChange(incident.id, {
                  severity: SEVERITY_DISPLAY[val.toLowerCase()] ?? val,
                })
              }
            />
          );
        },
      },
      {
        id: 'incident',
        header: 'Incident',
        width: 600,
        sortable: false,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => (
          <Flex sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
            <OverflowTooltip>{incident.title}</OverflowTooltip>
            {incident.monitors.length > 1 && (
              <Tooltip title="Merged — this incident contains multiple monitors">
                <TypeChip>
                  <SvgIcon width={14}>
                    <MergeIcon />
                  </SvgIcon>
                  <TypeChipLabel>Merged</TypeChipLabel>
                </TypeChip>
              </Tooltip>
            )}
          </Flex>
        ),
      },
      {
        id: 'start_time',
        header: 'First Event',
        showValueWhenEmpty: true,
        sortable: true,
        filterable: false,
        width: 130,
        type: ColumnType.Date,
        renderer: (incident) => (
          <OverflowTooltip>
            {localizeDate(incident.startTime).fromNow()}
          </OverflowTooltip>
        ),
      },
      {
        id: 'last_event',
        header: 'Last Event',
        showValueWhenEmpty: true,
        sortable: true,
        filterable: false,
        width: 130,
        type: ColumnType.Date,
        renderer: (incident) => (
          <OverflowTooltip>
            {incident.lastFailureTime
              ? localizeDate(incident.lastFailureTime).fromNow()
              : '—'}
          </OverflowTooltip>
        ),
      },
      {
        id: 'monitors',
        header: 'Monitors',
        width: 110,
        sortable: true,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => {
          const counts = countMonitorsByChipStatus(incident.monitors ?? []);
          const entries = [...counts.entries()]
            .filter(([, n]) => n > 0)
            .sort((a, b) => {
              const ia = MONITOR_CHIP_ORDER.indexOf(a[0]);
              const ib = MONITOR_CHIP_ORDER.indexOf(b[0]);
              if (ia === -1 && ib === -1) {
                return a[0].localeCompare(b[0]);
              }
              if (ia === -1) return 1;
              if (ib === -1) return -1;
              return ia - ib;
            });
          if (entries.length === 0) {
            return <Label>—</Label>;
          }
          return (
            <Flex sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              {entries.map(([status, count]) => (
                <StatusChip
                  key={status}
                  status={status}
                  label={String(count)}
                  size="small"
                />
              ))}
            </Flex>
          );
        },
      },
      {
        id: 'database',
        header: 'Database',
        width: 160,
        sortable: true,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => (
          <OptionalChipCounterList items={incident.databases} />
        ),
      },
      {
        id: 'schema',
        header: 'Schema',
        width: 160,
        sortable: true,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => (
          <OptionalChipCounterList items={incident.schemas} />
        ),
      },
      {
        id: 'asset_name',
        header: 'Asset Name',
        width: 160,
        sortable: true,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => (
          <OptionalChipCounterList items={incident.assetNames} />
        ),
      },
      {
        id: 'monitor_type',
        header: 'Monitor Type',
        width: 160,
        sortable: false,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => (
          <OptionalChipCounterList items={incident.monitorTypes} />
        ),
      },
      {
        id: GROUPING_STRATEGY_COLUMN_ID,
        header: 'Grouped by',
        width: 180,
        sortable: false,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => (
          <OverflowTooltip>
            {getGroupingStrategyDisplay(incident.groupingStrategy)}
          </OverflowTooltip>
        ),
      },
      ...(hasTicketingIntegration
        ? [
            {
              id: 'tickets',
              header: 'Tickets',
              width: 120,
              sortable: true,
              filterable: false,
              defaultUntoggled: true,
              renderer: (incident: IncidentListItem) => {
                if (!incident.tickets.length) return <Label>—</Label>;
                const first = incident.tickets[0];
                const rest = incident.tickets.slice(1);
                return (
                  <Flex sx={{ alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <TicketChipCompact ticket={first} />
                    {rest.length > 0 && (
                      <Tooltip
                        title={rest
                          .map((t) => getTicketDetails(t).label)
                          .join(', ')}
                      >
                        <Typography
                          sx={{
                            fontSize: fontSize('sm'),
                            fontWeight: fontWeight('medium'),
                          }}
                        >
                          +{rest.length}
                        </Typography>
                      </Tooltip>
                    )}
                  </Flex>
                );
              },
            },
          ]
        : []),
      {
        id: 'assignee',
        header: 'Assignee',
        width: FROZEN_COLUMN_WIDTH,
        sortable: true,
        filterable: false,
        sticky: 'right',
        showValueWhenEmpty: true,
        renderer: (incident) => {
          const userData = usersData?.find(
            (user) => user.email === incident.assignee,
          );
          const isDisabled = canEdit && incident.status === 'RESOLVED';
          return (
            <DropDown
              value={incident.assignee ?? undefined}
              menuButton={
                <Tooltip title={canEdit ? 'Set assignee' : 'Unassigned'}>
                  <div>
                    <IconButtonStyled
                      $readOnly={!canEdit}
                      tabIndex={canEdit ? undefined : -1}
                      disabled={isDisabled}
                    >
                      <Flex sx={{ alignItems: 'center', gap: 1 }}>
                        {canEdit ? <UserIcon /> : <Label>—</Label>}
                        {canEdit && (
                          <SvgIcon
                            width={10}
                            strokeColor="icon.tertiary"
                            fillColor="icon.tertiary"
                          >
                            <ArrowDownIcon />
                          </SvgIcon>
                        )}
                      </Flex>
                    </IconButtonStyled>
                  </div>
                </Tooltip>
              }
              showClear
              showSearch
              menuSelectedButton={
                <Tooltip title={incident.assignee ?? ''}>
                  <IconButtonStyled
                    $readOnly={!canEdit}
                    tabIndex={canEdit ? undefined : -1}
                    disabled={isDisabled}
                  >
                    <Flex sx={{ alignItems: 'center', gap: 1 }}>
                      {incident.assignee ? (
                        <Avatar
                          value={{
                            id: userData?.id || incident.assignee || '',
                            name: userData?.name || incident.assignee || '',
                          }}
                          size="small"
                        />
                      ) : (
                        <UserIcon />
                      )}
                      {canEdit && (
                        <SvgIcon
                          width={10}
                          strokeColor="icon.tertiary"
                          fillColor="icon.tertiary"
                        >
                          <ArrowDownIcon />
                        </SvgIcon>
                      )}
                    </Flex>
                  </IconButtonStyled>
                </Tooltip>
              }
              options={assigneeOptions || []}
              disabled={isDisabled || !canEdit}
              onChange={(val) => {
                onChange(incident.id, {
                  assignee:
                    assigneeOptions?.find((o) => o.value === val)?.value ||
                    NO_ASSIGNEE_VALUE,
                });
              }}
            />
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        width: FROZEN_COLUMN_WIDTH,
        sortable: true,
        filterable: false,
        sticky: 'right',
        showValueWhenEmpty: true,
        renderer: (incident) => {
          const displayStatus =
            STATUS_DISPLAY[incident.status.toLowerCase()] ?? 'Open';
          return (
            <Menu
              tooltip={canEdit ? 'Set status' : displayStatus}
              menuButton={
                <IconButtonStyled
                  $readOnly={!canEdit}
                  tabIndex={canEdit ? undefined : -1}
                  disabled={canEdit && incident.status === 'RESOLVED'}
                >
                  <Flex sx={{ alignItems: 'center', gap: 1 }}>
                    {smallStatusIconMap[displayStatus as Status]}
                    {canEdit && (
                      <SvgIcon
                        width={10}
                        strokeColor="icon.tertiary"
                        fillColor="icon.tertiary"
                      >
                        <ArrowDownIcon />
                      </SvgIcon>
                    )}
                  </Flex>
                </IconButtonStyled>
              }
              menuTitle="SET STATUS"
              options={statusOptions}
              closeOnChange
              onChange={(val) =>
                onChange(incident.id, {
                  status: STATUS_DISPLAY[val.toLowerCase()] ?? val,
                })
              }
            />
          );
        },
      },
      {
        id: 'actions',
        header: '',
        width: 56,
        sortable: false,
        filterable: false,
        metaColumn: true,
        nonToggleable: true,
        sticky: 'right',
        renderer: (incident) => (
          <IncidentActionsColumnMenu
            incident={incident}
            severityOptions={severityOptions}
            statusOptions={statusOptions}
            assigneeOptions={assigneeOptions}
            disabled={!canEdit}
            onChange={onChange}
            isAiIncidentsEnabled={isAiIncidentsEnabled}
            onOpenAIChat={onOpenAIChat}
          />
        ),
      },
    ],
    [
      severityOptions,
      statusOptions,
      assigneeOptions,
      usersData,
      canEdit,
      isAiIncidentsEnabled,
      hasTicketingIntegration,
      onOpenAIChat,
      onChange,
    ],
  );

export default useIncidentColumns;
